import { useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import Sidebar from '../../Sidebar';
import FormPageLayout from '../../components/FormPageLayout';
import PremiumPageHeader from '../../components/PremiumPageHeader';
import { displayError, displayErrorToast } from '../../helpers';
import {
  mergePdfs,
  imagesToPdf,
  excelToPdf,
  compressPdf,
  getPdfInfo,
  editPdf,
} from '../../services/UtilityServices';
import './PdfUtility.css';

const TABS = [
  { key: 'merge', label: 'Merge PDFs' },
  { key: 'image', label: 'Image → PDF' },
  { key: 'excel', label: 'Excel → PDF' },
  { key: 'compress', label: 'Compress' },
  { key: 'edit', label: 'Edit PDF' },
];

function FilePicker({ accept, multiple, files, onChange, hint }) {
  return (
    <div className="pdf-utility-picker">
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(event) => onChange(Array.from(event.target.files || []))}
      />
      {hint ? <p className="pdf-utility-hint">{hint}</p> : null}
      {files.length > 0 && (
        <ul className="pdf-utility-file-list">
          {files.map((file) => (
            <li key={`${file.name}-${file.size}`}>{file.name}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PdfUtility() {
  const [cookies] = useCookies(['myToken']);
  const [activeTab, setActiveTab] = useState('merge');
  const [busy, setBusy] = useState(false);
  const [mergeFiles, setMergeFiles] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [excelFile, setExcelFile] = useState([]);
  const [compressFile, setCompressFile] = useState([]);
  const [editFile, setEditFile] = useState([]);
  const [pageOrder, setPageOrder] = useState([]);
  const [rotations, setRotations] = useState({});
  const [stampText, setStampText] = useState('');

  const pageCount = pageOrder.length;

  const canRun = useMemo(() => {
    if (busy) return false;
    if (activeTab === 'merge') return mergeFiles.length >= 2;
    if (activeTab === 'image') return imageFiles.length >= 1;
    if (activeTab === 'excel') return excelFile.length === 1;
    if (activeTab === 'compress') return compressFile.length === 1;
    if (activeTab === 'edit') return editFile.length === 1 && pageOrder.length > 0;
    return false;
  }, [activeTab, busy, mergeFiles, imageFiles, excelFile, compressFile, editFile, pageOrder]);

  const loadEditInfo = async (files) => {
    setEditFile(files);
    setPageOrder([]);
    setRotations({});
    if (!files[0]) return;
    try {
      setBusy(true);
      const info = await getPdfInfo(cookies, files[0]);
      const order = (info.pages || []).map((page) => page.index);
      setPageOrder(order);
      const nextRotations = {};
      (info.pages || []).forEach((page) => {
        nextRotations[page.index] = 0;
      });
      setRotations(nextRotations);
    } catch (error) {
      displayError(error.response?.data || error.message, 'Could not read PDF');
    } finally {
      setBusy(false);
    }
  };

  const movePage = (index, direction) => {
    setPageOrder((current) => {
      const next = [...current];
      const target = index + direction;
      if (target < 0 || target >= next.length) return current;
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next;
    });
  };

  const removePage = (index) => {
    setPageOrder((current) => current.filter((_, i) => i !== index));
  };

  const rotatePage = (pageIndex) => {
    setRotations((current) => ({
      ...current,
      [pageIndex]: ((Number(current[pageIndex]) || 0) + 90) % 360,
    }));
  };

  const handleRun = async () => {
    if (!canRun) return;
    setBusy(true);
    try {
      if (activeTab === 'merge') {
        await mergePdfs(cookies, mergeFiles);
      } else if (activeTab === 'image') {
        await imagesToPdf(cookies, imageFiles);
      } else if (activeTab === 'excel') {
        await excelToPdf(cookies, excelFile[0]);
      } else if (activeTab === 'compress') {
        await compressPdf(cookies, compressFile[0]);
      } else if (activeTab === 'edit') {
        await editPdf(cookies, editFile[0], {
          pageOrder,
          rotations,
          stampText,
        });
      }
    } catch (error) {
      displayErrorToast(error.message || 'PDF utility failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
      <div id="page-wrap">
        <FormPageLayout>
          <PremiumPageHeader
            module="UTILITY"
            title="PDF Utility"
            subtitle="Merge, convert, compress, and lightly edit PDF files."
            modeLabel="TOOLS"
            homeLink="/Home"
            showNavActions={false}
          />

          <div className="pdf-utility-shell">
            <div className="pdf-utility-tabs">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`pdf-utility-tab${activeTab === tab.key ? ' is-active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="pdf-utility-panel">
              {activeTab === 'merge' && (
                <FilePicker
                  accept=".pdf,application/pdf"
                  multiple
                  files={mergeFiles}
                  onChange={setMergeFiles}
                  hint="Select 2 or more PDF files to merge in the chosen order."
                />
              )}

              {activeTab === 'image' && (
                <FilePicker
                  accept="image/*"
                  multiple
                  files={imageFiles}
                  onChange={setImageFiles}
                  hint="PNG, JPG, WEBP and similar images become one PDF."
                />
              )}

              {activeTab === 'excel' && (
                <FilePicker
                  accept=".xlsx,.xls,.csv"
                  multiple={false}
                  files={excelFile}
                  onChange={setExcelFile}
                  hint="Converts the first 500 rows of a spreadsheet into a PDF table."
                />
              )}

              {activeTab === 'compress' && (
                <FilePicker
                  accept=".pdf,application/pdf"
                  multiple={false}
                  files={compressFile}
                  onChange={setCompressFile}
                  hint="Creates a cleaned/compressed copy of the uploaded PDF."
                />
              )}

              {activeTab === 'edit' && (
                <>
                  <FilePicker
                    accept=".pdf,application/pdf"
                    multiple={false}
                    files={editFile}
                    onChange={loadEditInfo}
                    hint="Reorder, rotate, delete pages, and optionally stamp text."
                  />
                  {pageCount > 0 && (
                    <div className="pdf-utility-edit">
                      <label className="pdf-utility-stamp">
                        Stamp text (optional)
                        <input
                          type="text"
                          value={stampText}
                          onChange={(event) => setStampText(event.target.value)}
                          placeholder="e.g. APPROVED"
                        />
                      </label>
                      <ul className="pdf-utility-pages">
                        {pageOrder.map((pageIndex, orderIndex) => (
                          <li key={`${pageIndex}-${orderIndex}`}>
                            <span>
                              Page {pageIndex + 1}
                              {rotations[pageIndex] ? ` · ${rotations[pageIndex]}°` : ''}
                            </span>
                            <div className="pdf-utility-page-actions">
                              <button type="button" onClick={() => movePage(orderIndex, -1)}>Up</button>
                              <button type="button" onClick={() => movePage(orderIndex, 1)}>Down</button>
                              <button type="button" onClick={() => rotatePage(pageIndex)}>Rotate</button>
                              <button type="button" onClick={() => removePage(orderIndex)}>Remove</button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}

              <div className="pdf-utility-actions">
                <button
                  type="button"
                  className="company-form-action-btn"
                  disabled={!canRun}
                  onClick={handleRun}
                >
                  {busy ? 'Working…' : 'Download result'}
                </button>
              </div>
            </div>
          </div>
        </FormPageLayout>
      </div>
    </>
  );
}

export default PdfUtility;
